import React, { useState } from 'react';
import api from 'services/api';

function downloadCSV(data, filename) {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csv = [headers.join(';'), ...data.map(row => headers.map(h => row[h] ?? '').join(';'))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const DownloadIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TRIMESTRES = [
  { value: 'annuel', label: 'Annuel' },
  { value: 'T1', label: 'T1 (Jan - Mars)' },
  { value: 'T2', label: 'T2 (Avr - Juin)' },
  { value: 'T3', label: 'T3 (Juil - Sept)' },
  { value: 'T4', label: 'T4 (Oct - Dec)' },
];

function getTrimestreDates(annee, trimestre) {
  const an = parseInt(annee, 10);
  switch (trimestre) {
    case 'T1': return { debut: `${an}-01-01`, fin: `${an}-03-31`, moisDebut: 1, moisFin: 3 };
    case 'T2': return { debut: `${an}-04-01`, fin: `${an}-06-30`, moisDebut: 4, moisFin: 6 };
    case 'T3': return { debut: `${an}-07-01`, fin: `${an}-09-30`, moisDebut: 7, moisFin: 9 };
    case 'T4': return { debut: `${an}-10-01`, fin: `${an}-12-31`, moisDebut: 10, moisFin: 12 };
    default:   return { debut: `${an}-01-01`, fin: `${an}-12-31`, moisDebut: 1, moisFin: 12 };
  }
}

function ExportComptable() {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [trimestre, setTrimestre] = useState('annuel');
  const [loading, setLoading] = useState({});

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const handleExport = async (key, endpoint, filename) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      const { debut, fin, moisDebut, moisFin } = getTrimestreDates(annee, trimestre);
      let url;
      switch (key) {
        case 'bulletins':
          url = `/paie/bulletins?mois=${moisDebut}&mois_fin=${moisFin}&annee=${annee}`;
          break;
        case 'charges':
          url = `/charges?debut=${debut}&fin=${fin}`;
          break;
        case 'compte':
          url = `/compte-resultat?debut=${debut}&fin=${fin}&annee=${annee}`;
          break;
        case 'livre':
          url = `/paie/livre?debut=${debut}&fin=${fin}&annee=${annee}`;
          break;
        default:
          url = endpoint;
      }
      const res = await api.get(url);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      if (data.length === 0) {
        alert('Aucune donnee trouvee pour cette periode.');
        return;
      }
      const label = trimestre === 'annuel' ? annee : `${trimestre}_${annee}`;
      downloadCSV(data, `${filename}_${label}.csv`);
    } catch (err) {
      console.error('Erreur export:', err);
      // ⓘ Regle 62 : « Veuillez reessayer » etait un ordre, et un ordre inutile
      //   — il ne dit rien de ce qui a echoue. Le message porte la cause reelle.
      alert(`L'export n'a pas abouti : ${err?.response?.data?.message || err?.message || 'cause non renvoyee par le serveur'}.`);
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const cards = [
    {
      key: 'bulletins',
      title: 'Bulletins de paie',
      description: 'Export de tous les bulletins de paie pour la periode',
      filename: 'bulletins_paie',
    },
    {
      key: 'charges',
      title: 'Charges & Depenses',
      description: 'Liste de toutes les charges et depenses',
      filename: 'charges_depenses',
    },
    {
      key: 'compte',
      title: 'Compte de resultat',
      description: 'Etat financier complet de la periode',
      filename: 'compte_resultat',
    },
    {
      key: 'livre',
      title: 'Livre de paie',
      description: 'Recapitulatif mensuel de la masse salariale',
      filename: 'livre_paie',
    },
  ];

  return (
    <div>
<div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="content-card-title">Periode</div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Annee</label>
            <select
              className="gss-input"
              value={annee}
              onChange={e => setAnnee(e.target.value)}
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Trimestre</label>
            <select
              className="gss-input"
              value={trimestre}
              onChange={e => setTrimestre(e.target.value)}
            >
              {TRIMESTRES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        {cards.map(card => (
          <div className="content-card" key={card.key} style={{ textAlign: 'center', padding: '24px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#c0392b', marginBottom: '12px' }}>
                <DownloadIcon />
              </div>
              <div className="content-card-title" style={{ marginBottom: '8px' }}>{card.title}</div>
              <p style={{ color: '#888', fontSize: '14px' }}>
                {card.description}
              </p>
            </div>
            <button
              className="gss-btn gss-btn-primary"
              style={{ marginTop: '20px', width: '100%' }}
              disabled={loading[card.key]}
              onClick={() => handleExport(card.key, null, card.filename)}
            >
              {loading[card.key] ? 'Chargement...' : 'Telecharger CSV'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ExportComptable;
